import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, MessageSquare, Download, Clock, PenTool, AlertTriangle, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../utils/api';

const ClientHome = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalFiles: 0,
    unreadMessages: 0
  });
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchPendingSignatures();
  }, []);

  const fetchStats = async () => {
    try {
      const [files, messages] = await Promise.all([
        api.get('/files'),
        api.get('/messages/unread')
      ]);

      setStats({
        totalFiles: files.data.length,
        unreadMessages: messages.data.count
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSignatures = async () => {
    try {
      const response = await api.get('/signature-requests?status=pending');
      // Also get "viewed" status (started but not completed)
      const viewedResponse = await api.get('/signature-requests?status=viewed');
      const allPending = [...(response.data || []), ...(viewedResponse.data || [])];
      setPendingSignatures(allPending);
    } catch (error) {
      console.error('Failed to fetch pending signatures:', error);
    }
  };

  const quickActions = [
    {
      title: 'View Documents',
      description: 'Access your shared files',
      icon: FileText,
      color: 'from-teal-500 to-cyan-500',
      href: '/client/documents'
    },
    {
      title: 'Messages',
      description: 'Check your messages',
      icon: MessageSquare,
      color: 'from-purple-500 to-pink-500',
      href: '/client/messages'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="dashboard-title">Welcome Back!</h2>
        <p className="text-gray-600">Here's what's happening with your account</p>
      </div>

      {/* Pending Signatures Alert */}
      {pendingSignatures.length > 0 && (
        <div 
          className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 shadow-sm"
          data-testid="pending-signatures-alert"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <PenTool className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <h3 className="font-bold text-amber-800">Documents Awaiting Your Signature</h3>
              </div>
              <p className="text-amber-700 text-sm mb-4">
                You have {pendingSignatures.length} document{pendingSignatures.length > 1 ? 's' : ''} that require{pendingSignatures.length === 1 ? 's' : ''} your signature.
              </p>
              <div className="space-y-2">
                {pendingSignatures.map((sig) => (
                  <div 
                    key={sig.id}
                    className="bg-white rounded-lg p-4 border border-amber-100 flex items-center justify-between hover:border-amber-300 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-gray-900">{sig.title}</p>
                        <p className="text-xs text-gray-500">
                          {sig.document_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                          {sig.expires_at && ` • Expires ${new Date(sig.expires_at).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/sign/${sig.id}`)}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid={`sign-document-${sig.id}`}
                    >
                      Sign Now
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {pendingSignatures.length > 0 && (
          <div 
            className="glass rounded-xl p-6 border-l-4 border-amber-500 cursor-pointer hover:shadow-lg transition-shadow" 
            data-testid="stat-pending-signatures"
            onClick={() => pendingSignatures.length > 0 && navigate(`/sign/${pendingSignatures[0].id}`)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <PenTool className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-amber-600 mb-1">{pendingSignatures.length}</p>
            <p className="text-sm text-gray-600">Pending Signatures</p>
          </div>
        )}

        <div className="glass rounded-xl p-6" data-testid="stat-total-files">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-teal-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-1">{stats.totalFiles}</p>
          <p className="text-sm text-gray-600">Documents Available</p>
        </div>

        <div className="glass rounded-xl p-6" data-testid="stat-unread-messages">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-1">{stats.unreadMessages}</p>
          <p className="text-sm text-gray-600">Unread Messages</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <a
                key={action.title}
                href={action.href}
                data-testid={`quick-action-${index}`}
                className="glass rounded-xl p-6 hover-lift transition-all"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${action.color} rounded-lg flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{action.title}</h4>
                <p className="text-sm text-gray-600">{action.description}</p>
              </a>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Clock className="w-5 h-5 text-teal-600" />
          <h3 className="text-lg font-bold text-gray-900">Recent Activity</h3>
        </div>
        <div className="space-y-3">
          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-2 h-2 bg-teal-600 rounded-full mt-2"></div>
            <div>
              <p className="text-sm font-medium text-gray-900">Welcome to PPMT Portal</p>
              <p className="text-xs text-gray-500">Your account has been set up successfully</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientHome;
